import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-10">
      <h2 className="font-serif text-2xl text-sage-800">{title}</h2>
      <div className="mt-4 space-y-4 text-sage-700/90">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6">
      <h3 className="font-serif text-lg text-sage-800">{title}</h3>
      <div className="mt-2 space-y-3 text-sage-700/90">{children}</div>
    </div>
  );
}

export default function HipaaPolicy() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="HIPAA Notice of Privacy Practices"
        intro="This notice describes how medical information about you may be used and disclosed and how you can get access to this information. Please review it carefully."
      />

      <section className="bg-cream-50">
        <div className="section max-w-3xl">
          <p className="text-sm text-sage-600">Effective Date: February 16, 2026</p>
          <p className="mt-2 font-medium text-sage-800">
            Your Information. Your Rights. Our Responsibilities.
          </p>

          <Section title="Your Rights">
            <p>
              When it comes to your health information, you have certain rights. This section explains your rights and
              some of our responsibilities to help you.
            </p>

            <SubSection title="Get an Electronic or Paper Copy of Your Medical Record">
              <p>
                You can ask to see or get an electronic or paper copy of your medical record and other health
                information we have about you. Ask us how to do this.
              </p>
              <p>
                We will provide a copy or a summary of your health information, usually within 30 days of your request.
                We may charge a reasonable, cost-based fee.
              </p>
            </SubSection>

            <SubSection title="Inspect Your Records In Person">
              <p>
                You may request to inspect your protected health information in person at our dental office. During an
                in-person inspection, you may take notes or photographs of your own health information. We will not
                impose unreasonable measures on your ability to do so.
              </p>
            </SubSection>

            <SubSection title="Ask Us to Correct Your Medical Record">
              <p>
                You can ask us to correct health information about you that you think is incorrect or incomplete. Ask
                us how to do this. We may say "no" to your request, but we'll tell you why in writing within 60 days.
              </p>
            </SubSection>

            <SubSection title="Request Confidential Communications">
              <p>
                You can ask us to contact you in a specific way (for example, home or office phone) or to send mail to
                a different address. We will say "yes" to all reasonable requests.
              </p>
            </SubSection>

            <SubSection title="Ask Us to Limit What We Use or Share">
              <p>
                You can ask us not to use or share certain health information for treatment, payment, or our
                operations. We are not required to agree to your request, and we may say "no" if it would affect your
                care.
              </p>
              <p>
                If you pay for a service or health care item out-of-pocket in full, you can ask us not to share that
                information for the purpose of payment or our operations with your health insurer. We will say "yes"
                unless a law requires us to share that information.
              </p>
            </SubSection>

            <SubSection title="Get a Copy of This Privacy Notice">
              <p>
                You can ask for a paper copy of this notice at any time, even if you have agreed to receive the notice
                electronically. We will provide you with a paper copy promptly.
              </p>
            </SubSection>

            <SubSection title="Choose Someone to Act for You">
              <p>
                If you have given someone medical power of attorney or if someone is your legal guardian, that person
                can exercise your rights and make choices about your health information. We will make sure the person
                has this authority and can act for you before we take any action.
              </p>
            </SubSection>

            <SubSection title="File a Complaint If You Feel Your Rights Are Violated">
              <p>
                You can complain if you feel we have violated your rights by{" "}
                <Link to="/#contact" className="text-blush-500 underline hover:text-blush-600">
                  contacting us
                </Link>
                . You can also file a complaint with the U.S. Department of Health and Human Services Office for Civil
                Rights by sending a letter to 200 Independence Avenue, S.W., Washington, D.C. 20201, calling
                1-877-696-6775, or visiting{" "}
                <a
                  href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blush-500 underline hover:text-blush-600"
                >
                  hhs.gov
                </a>
                . We will not retaliate against you for filing a complaint.
              </p>
            </SubSection>
          </Section>

          <Section title="Your Choices">
            <p>
              For certain health information, you can tell us your choices about what we share. If you have a clear
              preference for how we share your information in the situations described below, talk to us. Tell us what
              you want us to do, and we will follow your instructions.
            </p>

            <SubSection title="In These Cases, You Have Both the Right and Choice to Tell Us To:">
              <ul className="ml-4 space-y-2">
                {[
                  "Share information with your family, close friends, or others involved in your care",
                  "Share information in a disaster relief situation",
                  "Include your information in a hospital directory",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span aria-hidden="true" className="text-blush-400">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                If you are not able to tell us your preference, for example if you are unconscious, we may go ahead and
                share your information if we believe it is in your best interest. We may also share your information
                when needed to lessen a serious and imminent threat to health or safety.
              </p>
            </SubSection>

            <SubSection title="In These Cases We Never Share Your Information Unless You Give Us Written Permission:">
              <ul className="ml-4 space-y-2">
                {[
                  "Marketing purposes",
                  "Sale of your information",
                  "Most sharing of psychotherapy notes",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span aria-hidden="true" className="text-blush-400">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </SubSection>

            <SubSection title="Fundraising">
              <p>
                We may contact you for fundraising efforts, but you can tell us not to contact you again.
              </p>
            </SubSection>
          </Section>

          <Section title="Special Protections for Certain Types of Information">
            <p>
              Some information, such as HIV-related information, genetic information, alcohol and/or substance use
              disorder treatment records, and mental health records may be entitled to special confidentiality
              protections under applicable state or federal law. We will abide by these special protections as they
              pertain to applicable cases involving these types of records.
            </p>

            <SubSection title="Substance Use Disorder (SUD) Treatment Information">
              <p>
                Federal law (42 CFR Part 2) provides additional privacy protections for records relating to substance
                use disorder diagnosis, treatment, or referral created by federally assisted programs. Our dental
                practice may receive these records as part of your health history or through coordination of care with
                other providers.
              </p>
              <p>
                Substance use disorder treatment records received from programs subject to 42 CFR Part 2, or testimony
                relaying the content of such records, shall not be used or disclosed in civil, criminal,
                administrative, or legislative proceedings against you unless based on your written consent or a court
                order issued after notice and an opportunity to be heard.
              </p>
            </SubSection>
          </Section>

          <Section title="Our Uses and Disclosures">
            <p>We typically use or share your health information in the following ways.</p>

            <SubSection title="Treat You">
              <p>
                We can use your health information and share it with other professionals who are treating you.
              </p>
            </SubSection>

            <SubSection title="Run Our Organization">
              <p>
                We can use and share your health information to run our practice, improve your care, and contact you
                when necessary.
              </p>
            </SubSection>

            <SubSection title="Bill for Your Services">
              <p>
                We can use and share your health information to bill and get payment from health plans or other
                entities. For example, we give information about you to your dental insurance plan so it will pay for
                your services.
              </p>
            </SubSection>

            <SubSection title="Other Permitted Uses and Disclosures">
              <p>We are also allowed or required to share your information in ways that contribute to the public good:</p>
              <ul className="ml-4 mt-2 space-y-2">
                {[
                  "Public health and safety issues (e.g., preventing disease, reporting adverse reactions to medications, reporting suspected abuse or neglect)",
                  "Health research",
                  "Compliance with the law",
                  "Responding to organ and tissue donation requests",
                  "Working with a medical examiner or funeral director",
                  "Workers' compensation, law enforcement, and other government requests",
                  "Responding to lawsuits and legal actions",
                ].map((item) => (
                  <li key={item} className="flex gap-2 text-sage-700/90">
                    <span aria-hidden="true" className="text-blush-400">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </SubSection>
          </Section>

          <Section title="Our Responsibilities">
            <ul className="ml-4 space-y-2">
              {[
                "We are required by law to maintain the privacy and security of your protected health information.",
                "We will let you know promptly if a breach occurs that may have compromised the privacy or security of your information.",
                "We must follow the duties and privacy practices described in this notice and give you a copy of it.",
                "We will not use or share your information other than as described here unless you tell us we can in writing.",
                "We are also required to comply with all applicable federal nondiscrimination laws, including laws that address language access requirements.",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-blush-400">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Changes to the Terms of This Notice">
            <p>
              We can change the terms of this notice, and the changes will apply to all information we have about you.
              The new notice will be available upon request, in our office, and on our website.
            </p>
          </Section>

          <Section title="Contact Information">
            <p>
              For questions about this notice, to exercise any of your rights, or to file a complaint, please{" "}
              <Link to="/#contact" className="text-blush-500 underline hover:text-blush-600">
                contact us
              </Link>
              .
            </p>
          </Section>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/privacy-policy" className="btn-primary">
              View Privacy Policy
            </Link>
            <Link to="/" className="btn-secondary">
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
